import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import zenscroll from 'zenscroll';
import { HotKeys } from 'react-hotkeys';
import styled from 'styled-components';
import { get, noop } from 'lodash';

import useInstance from 'utils/hooks/useInstance';
import usePreselection from './hooks/usePreselection';

import SelectableItem from './components/SelectableItem';

const keyMap = {
  select: 'enter',
  deselect: 'esc',
  moveUp: 'up',
  moveDown: 'down',
};

const SCROLLER_EDGE = 0;
const SCROLLER_DURATION = 700;

const StyledList = styled.ul`
  height: 100%;
  margin: 0;
  overflow: auto;
  padding: 0;
  scroll-behavior: smooth;
  position: relative;
  width: 100%;
`;

const StyledHotKeys = styled(HotKeys)`
  height: 100%;
  width: 100%;
`;

const createRefList = () => {
  const refs = [];

  return {
    get refs() {
      return refs;
    },
    add: (index, ref) => {
      refs[index] = ref;
    },
    remove: index => {
      refs[index] = null;
    },
  };
};

const SelectableList = props => {
  const {
    count,
    items,
    ItemComponent,
    updateItem,
    isReordering,
    selectedItem,
    selectItem,
    matchedRulesMap,
    toggleItemStatus,
    reorderItem,
    getRuleMatchedRows,
    duplicateItem,
    currentItemIndex,
    selectedItemGroup,
    deselectItem,
  } = props;

  let zenScroller;

  const selectableItemRefs = useInstance(createRefList());

  const scrollToElement = element => {
    zenScroller.to(element);
  };

  const scrollToIndex = index => {
    const element = selectableItemRefs.refs[index];

    setTimeout(() => {
      scrollToElement(element);
    }, 100);
  };

  const [
    preSelectedItemIndex,
    setPreSelectedItemIndex,
    preSelectPrevious,
    preSelectNext,
  ] = usePreselection(0, items.length, scrollToIndex);

  // When user clicks on an item selectedItem is changing.
  // We should move scroll to the new selectedItem.
  // If currentItemIndex is -1 it means that there is no selectableItem

  useEffect(() => {
    if (currentItemIndex === -1) return;
    scrollToIndex(currentItemIndex);
    setPreSelectedItemIndex(currentItemIndex);
  }, [selectedItem]);

  const handleListRef = listRef => {
    if (listRef) {
      zenScroller = zenscroll.createScroller(
        listRef,
        SCROLLER_DURATION,
        SCROLLER_EDGE,
      );
    }
  };

  const handleSelectItem = item => {
    if (selectedItem.id !== item.id) {
      selectItem(item);
    }
  };

  const selectPreselectedItem = () => {
    const preselectedItem = items[preSelectedItemIndex];
    selectItem(preselectedItem);
  };

  const getItemIsSelected = item => {
    const newSelectedItem = item.rules ? selectedItemGroup : selectedItem;

    return get(newSelectedItem, 'id') === item.id;
  };

  const hotKeysHandlers = {
    select: selectPreselectedItem,
    deselect: deselectItem,
    moveUp: preSelectPrevious,
    moveDown: preSelectNext,
  };

  const handleSelectableItemRef = (elem, index) => {
    if (elem) {
      selectableItemRefs.add(index, elem);
    } else {
      selectableItemRefs.remove(index);
    }
  };

  /* eslint-disable react/destructuring-assignment */

  return (
    <StyledHotKeys
      focused
      attach={window}
      handlers={hotKeysHandlers}
      keyMap={keyMap}
    >
      <StyledList ref={handleListRef} data-test={props['data-test']}>
        {items.map((model, index) => (
          <SelectableItem
            key={model.id}
            count={count}
            duplicateItem={duplicateItem}
            getRuleMatchedRows={getRuleMatchedRows}
            index={index}
            isArchived={model.archived}
            isPreSelected={preSelectedItemIndex === index}
            isReordering={isReordering}
            isSelected={getItemIsSelected(model)}
            ItemComponent={ItemComponent}
            matchedRulesMap={matchedRulesMap}
            model={model}
            reorderItem={reorderItem}
            selectItem={handleSelectItem}
            toggleItemStatus={toggleItemStatus}
            updateItem={updateItem}
            onMount={selectableItemRefs.add}
            onRef={handleSelectableItemRef}
            onUnmount={selectableItemRefs.remove}
          />
        ))}
      </StyledList>
    </StyledHotKeys>
  );
};

SelectableList.propTypes = {
  deselectItem: PropTypes.func.isRequired,
  ItemComponent: PropTypes.oneOfType([PropTypes.element]).isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedItem: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  selectItem: PropTypes.func.isRequired,
  count: PropTypes.number,
  currentItemIndex: PropTypes.number,
  'data-test': PropTypes.string,
  duplicateItem: PropTypes.func,
  getRuleMatchedRows: PropTypes.func,
  isReordering: PropTypes.bool,
  matchedRulesMap: PropTypes.shape({}),
  reorderItem: PropTypes.func,
  selectedItemGroup: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  toggleItemStatus: PropTypes.func,
  updateItem: PropTypes.func,
};

SelectableList.defaultProps = {
  count: 0,
  isReordering: false,
  currentItemIndex: 0,
  matchedRulesMap: {},
  updateItem: noop,
  reorderItem: noop,
  toggleItemStatus: noop,
  getRuleMatchedRows: noop,
  duplicateItem: noop,
};

export default SelectableList;
